import AddressAutocomplete from './AddressAutocomplete';
import { singleLocationAudience } from '@/services/audienceService';
import UserAudienceFields from '@/components/audience/UserAudienceFields';
import type { UserAudience } from '@/services/audienceService';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useState } from 'react';
import { useBranding, useRolesForDropdown, useTranslation } from '@/hooks';
import { Translation } from '@/locales';
import Input from '@/components/ui/Input';
import Button from '@/components/ui/Button';
import { CreateUserRequest, UserRoleObject } from '@/types';
import ApiSelect from '@/components/ui/ApiSelect.tsx';

const createUserSchema = (t: Translation) => z.object({
  username: z
    .string()
    .min(3, t.validation.minLength(3))
    .max(50, t.validation.maxLength(50))
    .regex(/^[a-zA-Z0-9_]+$/, t.validation.usernameFormat),
  fullName: z
    .string()
    .min(2, t.validation.minLength(2))
    .max(100, t.validation.maxLength(100)),
  email: z
    .union([
      z.email({ message: t.validation.email }),
      z.literal(''),
    ])
    .transform(v => (v === '' ? undefined : v))
    .optional(),
  phoneNumber: z
    .union([
      z
        .string()
        .min(8, t.validation.minLength(8))
        .max(20, t.validation.maxLength(20))
        .regex(/^[+]?[\d\s\-()]+$/, t.validation.phoneFormat),
      z.literal(''),
    ])
    .transform(v => (v === '' ? undefined : v))
    .optional(),
  addressCountryId: z
    .union([z.string().min(1), z.literal('')])
    .transform(v => (v === '' ? undefined : v))
    .optional(),
  addressAdminAreaId: z
    .union([z.string().min(1), z.literal('')])
    .transform(v => (v === '' ? undefined : v))
    .optional(),
  addressLocalityId: z
    .union([z.string().min(1), z.literal('')])
    .transform(v => (v === '' ? undefined : v))
    .optional(),
  address: z
    .string()
    .max(255, t.validation.maxLength(255))
    .optional(),
  comment: z
    .string()
    .max(1000, t.validation.maxLength(1000))
    .optional(),
  password: z
    .string()
    .min(4, t.validation.minLength(8))
    .max(128, t.validation.maxLength(128)),
  roleCode: z.string(t.users.validation.roleRequired),
  pricePer12Credits: z
    .string()
    .optional()
    .refine((value) => value === undefined || value === '' || (!Number.isNaN(Number(value)) && Number(value) >= 0), {
      message: 'El precio no puede ser negativo',
    }),
  pricePer12CreditsCurrency: z.enum(['USD', 'DOP']).optional(),
});

type CreateUserFormData = z.infer<ReturnType<typeof createUserSchema>>;

interface CreateUserFormProps {
  initialAudience?: UserAudience;
  onSuccess?: (data: CreateUserRequest) => void;
  isEdit?: boolean;
  isSubmitting: boolean
  onCancel?: () => void;
}

const CreateUserForm = ({ onSuccess, onCancel, isEdit, isSubmitting, initialAudience }: CreateUserFormProps) => {
  const { t } = useTranslation();
  const { branding } = useBranding();
  const [audience, setAudience] = useState<UserAudience>(singleLocationAudience(initialAudience));
  const [submitError, setSubmitError] = useState<string | null>(null);

  const {
    control,
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
    setValue,
  } = useForm<CreateUserFormData>({
    resolver: zodResolver(createUserSchema(t)),
    defaultValues: {
      roleCode: 'app',
      addressCountryId: '',
      addressAdminAreaId: '',
      addressLocalityId: '',
      pricePer12Credits: '',
      pricePer12CreditsCurrency: 'USD',
    }
  });

  const roleCodeValue = watch('roleCode');
  const showCreditPricingFields = roleCodeValue === 'agent' || roleCodeValue === 'superagent';

  const onSubmit = async (data: CreateUserFormData) => {
    setSubmitError(null);
    const normalizedPricePer12Credits = data.pricePer12Credits ? Number(data.pricePer12Credits) : undefined;

    try {
      await onSuccess?.({
        ...data,
        audience: audienceManagementEnabled ? singleLocationAudience(audience) : undefined,
        pricePer12Credits: showCreditPricingFields ? normalizedPricePer12Credits : undefined,
        pricePer12CreditsCurrency: showCreditPricingFields ? data.pricePer12CreditsCurrency : undefined,
      });
      if (!isEdit) {
        reset();
        setAudience(singleLocationAudience(initialAudience));
      }
    } catch (error) {
      setSubmitError(error instanceof Error ? error.message : t.errors.unexpectedError);
    }
  };

  const mapRolesToOptions = (data: unknown[]) =>
    (data as UserRoleObject[]).map(role => ({ value: role.code, label: role.name }));

  const audienceManagementEnabled = Boolean(branding.modules?.audienceManagementEnabled);

  return (
    <div className="bg-white rounded-lg shadow-lg p-6">
      <div className="mb-6">
        <h2 className="text-xl font-semibold text-gray-900">{t.createUser.title}</h2>
        <p className="text-gray-600 mt-1">{t.createUser.description}</p>
      </div>
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Input
            label={t.form.username}
            placeholder={t.placeholders.username}
            error={errors.username?.message}
            {...register('username')}
          />

          <Input
            label={t.form.fullName}
            placeholder={t.placeholders.fullName}
            error={errors.fullName?.message}
            {...register('fullName')}
          />

          <Input
            label={t.form.email}
            type="email"
            placeholder={t.placeholders.email}
            error={errors.email?.message}
            {...register('email')}
          />

          <Input
            label={t.form.phoneNumber}
            type="tel"
            placeholder={t.placeholders.phoneNumber}
            error={errors.phoneNumber?.message}
            {...register('phoneNumber')}
          />

          <Input
            label={t.form.password}
            type="password"
            placeholder={t.placeholders.password}
            error={errors.password?.message}
            helperText={t.helpers.password}
            {...register('password')}
          />

          <Controller
            control={control}
            name="roleCode"
            render={({ field }) => (
              <ApiSelect
                label={t.form.role}
                placeholder={t.placeholders.selectRole}
                useQueryHook={useRolesForDropdown}
                mapDataToOptions={mapRolesToOptions}
                error={errors.roleCode?.message}
                {...field}
              />
            )}
          />

          {showCreditPricingFields && (
            <Input
              label="Precio por 12 créditos"
              type="number"
              step="0.01"
              min="0"
              placeholder="0.00"
              error={errors.pricePer12Credits?.message}
              {...register('pricePer12Credits')}
            />
          )}

          {showCreditPricingFields && (
            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700">
                Moneda
              </label>
              <select
                className="w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                {...register('pricePer12CreditsCurrency')}
              >
                <option value="USD">USD</option>
                <option value="DOP">DOP</option>
              </select>
              {errors.pricePer12CreditsCurrency && (
                <p className="mt-1 text-sm text-red-600">{errors.pricePer12CreditsCurrency.message}</p>
              )}
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 gap-6">
          {audienceManagementEnabled && <UserAudienceFields value={audience} onChange={setAudience} disabled={isSubmitting} />}
          {audienceManagementEnabled && <AddressAutocomplete
            value={{ countryId: watch('addressCountryId') ?? '', adminAreaId: watch('addressAdminAreaId') ?? '', localityId: watch('addressLocalityId') ?? '' }}
            disabled={isSubmitting}
            error={errors.addressCountryId?.message || errors.addressAdminAreaId?.message || errors.addressLocalityId?.message}
            onChange={value => {
              setValue('addressCountryId', value.countryId, { shouldDirty: true });
              setValue('addressAdminAreaId', value.adminAreaId, { shouldDirty: true });
              setValue('addressLocalityId', value.localityId, { shouldDirty: true });
            }} />}

          <Input
            label="Dirección"
            placeholder="Ingresa la dirección del usuario (opcional)"
            error={errors.address?.message}
            {...register('address')}
          />

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Comentarios
            </label>
            <textarea
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
              rows={4}
              placeholder="Comentarios adicionales sobre el usuario (opcional)"
              {...register('comment')}
            />
            {errors.comment && (
              <p className="mt-1 text-sm text-red-600">{errors.comment.message}</p>
            )}
          </div>
        </div>

        {submitError && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md">
            <p className="text-sm text-red-600">{submitError}</p>
          </div>
        )}

        <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200">
          {onCancel && (
            <Button
              type="button"
              variant="outline"
              onClick={onCancel}
              disabled={isSubmitting}
            >
              {t.buttons.cancel}
            </Button>
          )}
          <Button
            type="submit"
            loading={isSubmitting}
            disabled={isSubmitting}
          >
            {isSubmitting ? t.buttons.creating : t.buttons.create}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default CreateUserForm;
